import type { CustomEventProperties, JsonPrimitive } from './contracts';
import { INTERACTION_PROPERTY } from './interactions';

// Mirrors the tracking service's website-tracking/properties.contract.ts.
const MAX_PROPERTIES = 32;
const KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
const DENIED_KEYS = new Set([
  INTERACTION_PROPERTY,
  'name', 'event', 'uuid', 'timestamp', 'distinct_id', 'session_id',
  'email', 'e_mail', 'mail', 'phone', 'telephone', 'mobile',
  'first_name', 'last_name', 'full_name', 'address', 'street', 'zip', 'postal_code',
  'ip', 'ip_address', 'user_agent', 'password', 'token', 'api_key', 'secret',
  'ssn', 'iban', 'card_number', 'birthday', 'date_of_birth',
  'identity', 'traits', 'user_id', 'visitor_id'
]);

type PropertiesResult =
  | { status: 'valid'; properties: CustomEventProperties }
  | { status: 'invalid'; reason: 'invalid_object' | 'too_many_properties' | 'invalid_key' | 'reserved_key' | 'invalid_value' };

export const readCustomEventProperties = (input: unknown): PropertiesResult => {
  if (input === undefined) return { status: 'valid', properties: {} };
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return { status: 'invalid', reason: 'invalid_object' };
  }
  const prototype: unknown = Object.getPrototypeOf(input);
  if (prototype !== Object.prototype && prototype !== null) {
    return { status: 'invalid', reason: 'invalid_object' };
  }
  const keys = Object.keys(input);
  if (keys.length > MAX_PROPERTIES) return { status: 'invalid', reason: 'too_many_properties' };
  const properties: Record<string, JsonPrimitive> = {};
  for (const key of keys) {
    if (!KEY_PATTERN.test(key)) return { status: 'invalid', reason: 'invalid_key' };
    if (DENIED_KEYS.has(key)) return { status: 'invalid', reason: 'reserved_key' };
    // Accessors are not business data. Do not invoke getters supplied by the host.
    const descriptor = Object.getOwnPropertyDescriptor(input, key);
    if (!descriptor || !('value' in descriptor)) return { status: 'invalid', reason: 'invalid_value' };
    const raw: unknown = descriptor.value;
    const value = typeof raw === 'string' ? raw.trim() : raw;
    if (key === 'value' && typeof value !== 'number') return { status: 'invalid', reason: 'invalid_value' };
    if (key === 'currency') {
      if (typeof value !== 'string' || !/^[A-Z]{3}$/.test(value.toUpperCase())) {
        return { status: 'invalid', reason: 'invalid_value' };
      }
      properties[key] = value.toUpperCase();
    } else if (typeof value === 'string' && value.length <= 500) {
      properties[key] = value;
    } else if (typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= 1e15) {
      properties[key] = value;
    } else if (value === null || typeof value === 'boolean') {
      properties[key] = value;
    } else {
      return { status: 'invalid', reason: 'invalid_value' };
    }
  }
  return { status: 'valid', properties };
};

/** Fixed UI labels only. Customers must not put user data into these labels. */
export const anonymousCustomEventProperties = (
  properties: Readonly<Record<string, unknown>>
): Record<string, string | number> => {
  const result: Record<string, string | number> = {};
  for (const key of ['step', 'method', 'position', 'plan', 'variant']) {
    const value = properties[key];
    if (typeof value === 'string' && /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,99}$/.test(value.trim())) {
      result[key] = value.trim();
    }
  }
  const stepNumber = properties.step_number;
  if (typeof stepNumber === 'number' && Number.isInteger(stepNumber) && stepNumber >= 0 && stepNumber <= 1_000) {
    result.step_number = stepNumber;
  }
  return result;
};
